import { type FC, useState } from "react";
import { Platform, Pressable, type ViewStyle } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import "../../../theme";
import { Text } from "../../Text";
import { CONTROL_SIZE } from "../pagination-metrics";

export interface PaginationPageButtonProps {
  readonly page: number;
  readonly selected: boolean;
  readonly disabled: boolean;
  readonly onPress: () => void;
  readonly accessibilityLabel: string;
}

const PaginationPageButton: FC<PaginationPageButtonProps> = ({
  page,
  selected,
  disabled,
  onPress,
  accessibilityLabel,
}) => {
  const [hovered, setHovered] = useState(false);
  const { theme } = useUnistyles();

  // Light and dark use different accents from the design (navy vs. purple)
  // rather than the shared button token — this active fill is specific to
  // pagination, not the app's primary action color. Computed here rather than
  // inside StyleSheet.create: unistyles' reactivity compiler doesn't reliably
  // track a `theme.currentTheme` ternary evaluated inside a stylesheet factory.
  const selectedBackground = theme.currentTheme === "dark" ? theme.colors.navy[300] : theme.colors.navy[700];

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      onHoverIn={() => {
        setHovered(true);
      }}
      onHoverOut={() => {
        setHovered(false);
      }}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ selected, disabled }}
      // The ARIA pagination pattern marks the page you are on with
      // `aria-current`, not `aria-selected` — these are navigation controls,
      // not a tab list or a listbox.
      aria-current={selected ? "page" : undefined}
      style={({ pressed }) => [
        styles.button,
        hovered && !selected && !disabled && styles.buttonHovered,
        pressed && !selected && !disabled && styles.buttonPressed,
        selected && [styles.buttonSelected, { backgroundColor: selectedBackground }],
        disabled && styles.buttonDisabled,
        Platform.OS === "web" && !disabled && styles.webInteractive,
      ]}
    >
      {/* The selected label takes its colour from the button palette rather than
          a Text colour token: the token that reads as "on primary" differs per
          theme, and the button background is the same primary in both. */}
      <Text textStyle={selected ? "sm/semibold" : "sm/medium"} color="text" style={selected && styles.selectedLabel}>
        {page.toString()}
      </Text>
    </Pressable>
  );
};

PaginationPageButton.displayName = "PaginationPageButton";

export { PaginationPageButton };

const styles = StyleSheet.create((theme) => ({
  button: {
    minWidth: CONTROL_SIZE,
    height: CONTROL_SIZE,
    paddingHorizontal: theme.spacing.xs,
    borderRadius: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    alignItems: "center",
    justifyContent: "center",
  },
  buttonHovered: {
    backgroundColor: theme.colors.surface2.hover,
  },
  buttonPressed: {
    backgroundColor: theme.colors.surface2.selected,
  },
  buttonSelected: {
    borderColor: "transparent",
  },
  buttonDisabled: {
    opacity: 0.4,
  },
  selectedLabel: {
    color: theme.colors.interactive.buttons.default_foreground,
  },
  webInteractive: Object.assign({ cursor: "pointer" } satisfies ViewStyle, {
    transition: "background-color 0.15s ease-in-out",
  }),
}));
